/* SKRT native gift coordinator. Shopify, not this file, authorizes the discount.
 * Staged mode is read-only. Active mode requires a nonzero catalog price.
 * Customer-buys scope mirrors collection 315793801295: no VERNA or Gift Card type.
 * Only our explicitly marked promotional lines may be changed or removed.
 */
(function (root) {
  'use strict';

  const MARKER = '_skrt_native_gift';
  const amount = (value) => Number.isSafeInteger(Number(value)) && Number(value) >= 0 ? Number(value) : 0;
  const allocationTotal = (line) => (line.line_level_discount_allocations || [])
    .reduce((sum, entry) => sum + amount(entry.amount), 0);
  const isGift = (line, cfg) => Number(line.variant_id) === cfg.variantId;
  const isManaged = (line, cfg) => isGift(line, cfg) && line.properties && line.properties[MARKER] === '1';

  function qualifyingLine(line, cfg) {
    return !isGift(line, cfg) && Number(line.product_id) !== cfg.productId &&
      line.gift_card !== true && line.requires_shipping === true &&
      typeof line.vendor === 'string' && line.vendor !== 'VERNA' &&
      typeof line.product_type === 'string' && line.product_type !== 'Gift Card' &&
      !line.selling_plan_allocation && amount(line.original_line_price) > 0;
  }

  function confirmedLine(line, cfg) {
    if (!isGift(line, cfg) || amount(line.quantity) !== 1 ||
        amount(line.original_line_price) === 0 || Number(line.final_line_price) !== 0) return false;
    const giftDiscount = (line.line_level_discount_allocations || []).filter((entry) =>
      entry.discount_application && entry.discount_application.title === cfg.discountTitle
    ).reduce((sum, entry) => sum + amount(entry.amount), 0);
    return giftDiscount >= amount(line.original_line_price);
  }

  function fingerprint(cart, cfg) {
    // Exclude gift lines and order-discount AMOUNTS: a provisional paid gift changes those
    // amounts. That must not cause a failed add/remove cycle to retry forever.
    return JSON.stringify([
      cart.currency,
      (cart.items || []).filter((line) => !isGift(line, cfg)).map((line) => [
        line.variant_id, line.quantity, line.original_line_price, line.final_line_price,
        allocationTotal(line), line.vendor, line.product_type
      ]),
      (cart.cart_level_discount_applications || []).map((d) => [d.title, d.type, d.value, d.value_type]),
      (cart.discount_codes || []).map((d) => [d.code, d.applicable])
    ]);
  }

  function planGift(cart, cfg, state = {}) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const gifts = items.filter((line) => isGift(line, cfg));
    const managed = gifts.filter((line) => isManaged(line, cfg));
    const giftQuantity = gifts.reduce((sum, line) => sum + amount(line.quantity), 0);
    const qualifyingSubtotal = items.filter((line) => qualifyingLine(line, cfg))
      .reduce((sum, line) => sum + amount(line.original_line_price), 0);
    // A zero-dollar allocation can still reserve a paid BOGO item for that offer.
    // Keep this display basis separate from the gross candidate used for native trials.
    const displaySubtotal = items.filter((line) => qualifyingLine(line, cfg) &&
      !(line.line_level_discount_allocations || []).some((entry) =>
        !entry.discount_application || entry.discount_application.title !== cfg.discountTitle
      )).reduce((sum, line) => sum + amount(line.original_line_price), 0);
    const currencySupported = cart.currency === cfg.currency;
    const hasPaidItem = amount(cart.total_price) > 0 && items.some((line) =>
      !isGift(line, cfg) && line.requires_shipping === true && line.gift_card !== true &&
      amount(line.final_line_price) > 0
    );
    // This is a candidate check, never checkout entitlement. Shopify can replace a
    // competing product discount with this offer. The runtime must reject a trial
    // that increases the cart total. Outside USD, never guess an exchange rate:
    // try once for positive qualifying merchandise and trust native allocation.
    const currencyValid = typeof cart.currency === 'string' && /^[A-Z]{3}$/.test(cart.currency);
    const eligible = currencyValid && hasPaidItem && qualifyingSubtotal > 0 &&
      (!currencySupported || qualifyingSubtotal >= cfg.threshold);
    const active = cfg.mode === 'active' && cfg.price > 0;
    const confirmed = active && eligible && cfg.available && giftQuantity === 1 &&
      gifts.length === 1 && confirmedLine(gifts[0], cfg);
    const blocked = state.blockedFingerprint === fingerprint(cart, cfg);
    let action = null;
    if (active) {
      if ((!eligible || !cfg.available) && managed.length) {
        action = { type: 'change', key: managed[managed.length - 1].key, quantity: 0 };
      } else if (giftQuantity > 1 && managed.length) {
        const last = managed[managed.length - 1];
        action = { type: 'change', key: last.key, quantity: gifts.length === 1 ? 1 : 0 };
      } else if (managed.length && !confirmed) {
        action = { type: 'change', key: managed[0].key, quantity: 0, rejected: true };
      } else if (eligible && cfg.available && !giftQuantity && !state.dismissed && !blocked) {
        action = { type: 'add', quantity: 1 };
      }
    }
    return { active, eligible, confirmed, currencySupported, qualifyingSubtotal, displaySubtotal,
      giftQuantity, managedQuantity: managed.reduce((sum, line) => sum + amount(line.quantity), 0),
      blocked, action };
  }

  const policy = { planGift, qualifyingLine, confirmedLine, fingerprint, MARKER };
  if (typeof module !== 'undefined' && module.exports) { module.exports = policy; return; }
  if (!root || root.__skrtNativeGift) return;
  root.__skrtNativeGift = true;
  const doc = root.document;
  let running = null, queued = false, timer = null, lastCart = null, pending = false;
  let blockedFingerprint = null, blockedReason = null, retryRequested = false, manualDismissal = null;
  let memoryPreference = null;
  let unresolved = false;
  const externalOperations = new Set(root.skrtCartOperations && root.skrtCartOperations.pendingIds || []);
  const storageKey = 'skrt_native_gift_v1';

  function config() {
    const node = doc.querySelector('[data-skrt-native-gift-config]');
    if (!node) return null;
    try {
      const cfg = JSON.parse(node.textContent);
      if (cfg.variantId !== 46563960291407 || cfg.productId !== 9036472090703 ||
          cfg.currency !== 'USD' || !Number.isSafeInteger(cfg.threshold) || cfg.threshold <= 0 ||
          !['staged', 'active'].includes(cfg.mode)) return null;
      return cfg;
    } catch (_) { return null; }
  }

  function preference(cart) {
    let saved;
    try { saved = JSON.parse(root.sessionStorage.getItem(storageKey) || 'null'); } catch (_) {}
    const prior = saved && saved.token === cart.token ? saved :
      memoryPreference && memoryPreference.token === cart.token ? memoryPreference : null;
    const dismissed = manualDismissal !== null ? manualDismissal :
      prior ? prior.dismissed : (cart.attributes || {}).skrt_gift_opt_out === '1';
    memoryPreference = { token: cart.token, dismissed, acknowledgedReview: prior && prior.acknowledgedReview,
      trial: prior && prior.trial };
    try { root.sessionStorage.setItem(storageKey, JSON.stringify(memoryPreference)); } catch (_) {}
    manualDismissal = null;
    return { dismissed: Boolean(dismissed), blockedFingerprint, blockedReason,
      acknowledgedReview: memoryPreference.acknowledgedReview, trial: memoryPreference.trial };
  }

  function savePreference(cart, fields) {
    memoryPreference = { ...(memoryPreference || {}), token: cart.token, ...fields };
    try { root.sessionStorage.setItem(storageKey, JSON.stringify(memoryPreference)); } catch (_) {}
  }

  function purchaseFingerprint(cart, cfg) {
    const lines = (cart.items || []).filter((line) => !isGift(line, cfg)).map((line) => [
      line.variant_id, line.quantity, line.original_line_price,
      line.selling_plan_allocation && line.selling_plan_allocation.selling_plan ? line.selling_plan_allocation.selling_plan.id : null,
      Object.entries(line.properties || {}).sort(([a], [b]) => a.localeCompare(b))
    ]).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    return JSON.stringify([cart.currency, lines, (cart.discount_codes || []).map((entry) => entry.code).sort()]);
  }

  function economics(cart, cfg, state) {
    if (!(cart.items || []).some((line) => isManaged(line, cfg))) return { safe: true };
    const baseline = state.trial;
    if (!baseline || baseline.token !== cart.token || baseline.identity !== purchaseFingerprint(cart, cfg) ||
        !Number.isSafeInteger(baseline.total) || baseline.total < 0) return { safe: false, rebaseline: true };
    return { safe: cart.total_price <= baseline.total, cost: cart.total_price > baseline.total };
  }

  function reviewGift(cart, cfg, state) {
    const unmarked = (cart.items || []).filter((line) => isGift(line, cfg) && !isManaged(line, cfg));
    const paid = unmarked.filter((line) => amount(line.final_line_price) > 0);
    const key = paid.length ? JSON.stringify([cart.token, cart.currency, paid.map((line) =>
      [line.variant_id, line.quantity, line.original_line_price, line.final_line_price])]) : null;
    return {
      key,
      amount: paid.reduce((sum, line) => sum + amount(line.final_line_price), 0),
      required: Boolean(key && key !== state.acknowledgedReview),
      // An unexplained zero is not proof that the native discount will survive checkout.
      unknown: unmarked.some((line) => amount(line.final_line_price) === 0 && !confirmedLine(line, cfg))
    };
  }

  function busy(value) {
    for (const node of doc.querySelectorAll('[data-skrt-gwp]')) node.setAttribute('aria-busy', value ? 'true' : 'false');
  }

  function validCart(cart) {
    if (!cart || typeof cart.token !== 'string' || !cart.token || !Array.isArray(cart.items) || typeof cart.currency !== 'string' ||
        !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 ||
        cart.items.some((line) => !line || typeof line.key !== 'string' ||
          !Number.isSafeInteger(line.quantity) || line.quantity <= 0 ||
          !Number.isSafeInteger(line.original_line_price) || line.original_line_price < 0 ||
          !Number.isSafeInteger(line.final_line_price) || line.final_line_price < 0)) {
      throw new Error('Invalid cart response');
    }
    return cart;
  }

  function route(path) {
    const prefix = root.Shopify && root.Shopify.routes ? root.Shopify.routes.root : '/';
    const url = new URL(prefix + path, root.location.origin);
    if (url.origin !== root.location.origin) throw new Error('Unexpected cart origin');
    return url.href;
  }

  async function request(path, body) {
    const controller = new AbortController();
    const timeout = root.setTimeout(() => controller.abort(), 10000);
    try {
      const response = await root.fetch(route(path), {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined, signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart request failed: ' + response.status);
      return await response.json();
    } finally { root.clearTimeout(timeout); }
  }

  function money(cents, cfg) {
    try { return new Intl.NumberFormat(cfg.locale || 'en-US', {
      style: 'currency', currency: cfg.currency
    }).format(cents / 100); } catch (_) { return '$' + (cents / 100).toFixed(2) + ' USD'; }
  }

  function render(cart, cfg, state, failed = false) {
    const result = planGift(cart, cfg, state);
    if (!economics(cart, cfg, state).safe) result.confirmed = false;
    const review = result.active ? reviewGift(cart, cfg, state) : { required: false, unknown: false };
    for (const node of doc.querySelectorAll('[data-skrt-gwp]')) {
      const message = (key) => node.getAttribute('data-gift-' + key) || '';
      const text = node.querySelector('.skrt-gwp__msg');
      const bar = node.querySelector('.skrt-gwp__bar');
      const restore = node.querySelector('[data-skrt-gift-restore]');
      const acknowledge = node.querySelector('[data-skrt-gift-acknowledge]');
      node.classList.toggle('is-unlocked', result.confirmed && !failed);
      node.setAttribute('data-gift-in-cart', result.confirmed && !failed ? 'true' : 'false');
      let key = 'progress';
      if (review.required) key = 'review';
      else if (failed || review.unknown) key = 'error';
      else if (result.confirmed) key = 'confirmed';
      else if (review.key) key = 'paid';
      else if (!cfg.available) key = 'unavailable';
      else if (cfg.mode === 'active' && !result.active) key = 'pending';
      else if (result.blocked && state.blockedReason === 'cost') key = 'cost';
      else if (result.blocked || (result.active && result.giftQuantity && !result.confirmed)) key = 'blocked';
      else if (!result.currencySupported) key = 'currency';
      else if (result.eligible && state.dismissed) key = 'removed';
      else if (result.eligible) key = cfg.mode === 'staged' ? 'qualified' : 'adding';
      const displayedAmount = review.key ? money(review.amount, { ...cfg, currency: cart.currency }) :
        money(Math.max(0, cfg.threshold - result.displaySubtotal), cfg);
      if (text) text.textContent = message(key).replace('[amount]', displayedAmount);
      if (bar) {
        const progress = result.currencySupported ? Math.min(100, result.displaySubtotal / cfg.threshold * 100) : 0;
        bar.style.setProperty('--progress', progress + '%');
        bar.setAttribute('aria-valuenow', String(Math.round(progress)));
        bar.hidden = !result.currencySupported;
      }
      if (restore) {
        restore.hidden = !result.active || review.required || (!failed && (!result.eligible || !cfg.available || result.confirmed || (!state.dismissed && !result.blocked)));
        restore.textContent = message(failed || result.blocked ? 'retry' : 'restore');
      }
      if (acknowledge) {
        acknowledge.hidden = !review.required;
        acknowledge.textContent = message('acknowledge').replace('[amount]', displayedAmount);
      }
    }
    return result;
  }

  async function refreshViews(cart) {
    // cart:refresh refreshes Concept's drawer only. Re-render the main cart section too,
    // without a page reload that could lose opt-out/rejection state or loop after rejection.
    const main = doc.querySelector('main-cart[data-section-id]');
    let refreshError = null;
    if (main) {
      try {
        const id = main.getAttribute('data-section-id');
        const sections = await request('cart?sections=' + encodeURIComponent(id));
        if (typeof sections[id] !== 'string') throw new Error('Cart section refresh failed');
        const html = new DOMParser().parseFromString(sections[id], 'text/html');
        const current = doc.getElementById('shopify-section-' + id);
        const next = html.getElementById('shopify-section-' + id);
        if (!current || !next) throw new Error('Cart section was not returned');
        current.innerHTML = next.innerHTML;
      } catch (error) { refreshError = error; }
    }
    if (root.theme && root.theme.pubsub) {
      // A cart-only event updates counts without invoking the theme's focus-moving section swap.
      root.theme.pubsub.publish(root.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'skrt-native-gift', cart });
    }
    doc.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false, source: 'skrt-native-gift' } }));
    if (refreshError) throw refreshError;
  }

  async function reconcile() {
    const cfg = config();
    if (!cfg || !doc.querySelector('[data-skrt-gwp]') || externalOperations.size) return;
    let changed = false;
    let cart = lastCart;
    let state = cart ? preference(cart) : {};
    const active = cfg.mode === 'active' && cfg.price > 0;
    try {
      cart = validCart(await request('cart.js')); lastCart = cart;
      if (externalOperations.size) return;
      state = preference(cart);
      if (retryRequested) {
        blockedFingerprint = null; blockedReason = null; retryRequested = false;
        state.blockedFingerprint = null; state.blockedReason = null;
      }
      if (!active) { unresolved = false; render(cart, cfg, state); return; }
      for (let i = 0; i < 12; i += 1) {
        if (externalOperations.size) return;
        const result = planGift(cart, cfg, state);
        const economic = economics(cart, cfg, state);
        const managed = (cart.items || []).find((line) => isManaged(line, cfg));
        // The gift must not replace a better promotion and increase the customer's bill.
        // Unknown baselines are rebuilt by removing only our own marked line first.
        const action = managed && (economic.rebaseline || (economic.cost && result.confirmed)) ? {
          type: 'change', key: managed.key, quantity: 0,
          rebaseline: economic.rebaseline, rejected: economic.cost, cost: economic.cost
        } : result.action;
        if (!action) break;
        unresolved = true;
        changed = true; // A failed/lost response can still have changed Shopify's cart.
        if (action.rejected) {
          blockedFingerprint = fingerprint(cart, cfg);
          blockedReason = action.cost ? 'cost' : 'discount';
          state.blockedFingerprint = blockedFingerprint;
          state.blockedReason = blockedReason;
        }
        if (action.type === 'add') {
          savePreference(cart, { trial: { token: cart.token, identity: purchaseFingerprint(cart, cfg), total: cart.total_price } });
          state = preference(cart);
          await request('cart/add.js', { items: [{ id: cfg.variantId, quantity: 1, properties: { [MARKER]: '1' } }] });
        } else {
          await request('cart/change.js', { id: action.key, quantity: action.quantity });
        }
        // Every mutation can change line keys and discount allocation. Never reuse old keys.
        cart = validCart(await request('cart.js')); lastCart = cart;
        // A competing promotion can make the cart ineligible only while the provisional gift
        // is present. Block on the settled, post-removal cart too, not its transient line keys.
        if (action.type === 'change' && action.quantity === 0) {
          if (!(cart.items || []).some((line) => isManaged(line, cfg))) savePreference(cart, { trial: null });
          if (!action.rebaseline) blockedFingerprint = fingerprint(cart, cfg);
        }
        state = preference(cart);
      }
      const final = planGift(cart, cfg, state);
      if (final.action) throw new Error('Gift cart could not settle');
      const review = reviewGift(cart, cfg, state);
      const economic = economics(cart, cfg, state);
      unresolved = (final.managedQuantity > 0 && (!final.confirmed || !economic.safe)) || review.required || review.unknown;
      render(cart, cfg, state, review.unknown || (unresolved && !review.required));
    } catch (error) {
      try {
        cart = validCart(await request('cart.js')); lastCart = cart;
        state = preference(cart);
        const result = planGift(cart, cfg, state);
        const review = reviewGift(cart, cfg, state);
        const economic = economics(cart, cfg, state);
        unresolved = active && ((result.managedQuantity > 0 && (!result.confirmed || !economic.safe)) || review.required || review.unknown);
        // Prevent a background retry loop. A deliberate Retry can recover an uncertain cart.
        if (changed) {
          blockedFingerprint = fingerprint(cart, cfg);
          state.blockedFingerprint = blockedFingerprint;
        }
      } catch (_) { unresolved = active; }
      // A lost response can still have committed a successful, free gift. Do not label
      // that verified cart a failure or add another pair. Unknown carts remain guarded.
      const fallback = cart || { items: [], currency: cfg.currency, total_price: 0, attributes: {} };
      const verifiedGift = active && !unresolved && planGift(fallback, cfg, state).confirmed;
      render(fallback, cfg, state, !verifiedGift);
    } finally {
      if (changed && cart && !externalOperations.size) {
        try { await refreshViews(cart); }
        catch (_) {
          // A display refresh is not a cart write failure. Retain the checkout decision
          // from the authoritative cart, while exposing a retry for the stale display.
          render(cart, cfg, state, true);
        }
      }
    }
  }

  function synchronize() {
    queued = true;
    if (running) return running;
    pending = true; busy(true);
    running = (async () => {
      try {
        while (queued) {
          queued = false;
          const cfg = config();
          if (root.skrtCartOperations && typeof root.skrtCartOperations.run === 'function') {
            await root.skrtCartOperations.run(reconcile);
          } else if (cfg && cfg.mode === 'active' && cfg.price > 0) {
            // A partial deployment must not create an independent cart writer.
            throw new Error('Shared cart coordinator unavailable');
          } else await reconcile(); // Staged fallback is read-only and needs no writer lock.
        }
      } catch (_) {
        const cfg = config();
        unresolved = Boolean(cfg && cfg.mode === 'active' && cfg.price > 0);
        if (cfg) render(lastCart || { items: [], currency: cfg.currency, total_price: 0, attributes: {} }, cfg,
          lastCart ? preference(lastCart) : {}, true);
        queued = false;
      } finally { running = null; pending = externalOperations.size > 0; busy(pending); }
    })();
    return running;
  }

  function schedule(event) {
    if (event && event.detail && event.detail.source === 'skrt-native-gift') return;
    root.clearTimeout(timer); timer = null;
    pending = true; busy(true);
    if (externalOperations.size) return;
    if (running) { queued = true; return; }
    timer = root.setTimeout(() => { timer = null; return synchronize(); }, 150);
  }
  function giftKey(key) { return String(key || '').split(':')[0] === '46563960291407'; }
  function storePath(value, paths) {
    if (!value) return false;
    try {
      const url = new URL(value, root.location.href || root.location.origin);
      if (url.origin !== root.location.origin) return false;
      return paths.some((path) => {
        const expected = new URL(route(path)).pathname.replace(/\/$/, '');
        const unlocalized = '/' + path;
        return url.pathname === expected || url.pathname.startsWith(expected + '/') ||
          url.pathname === unlocalized || url.pathname.startsWith(unlocalized + '/');
      });
    } catch (_) { return false; }
  }
  function checkoutSubmit(event) {
    const form = event.target;
    if (!form || !form.getAttribute) return false;
    const submitter = event.submitter;
    const destination = submitter && submitter.getAttribute('formaction') || form.getAttribute('action');
    if (storePath(destination, ['checkout', 'checkouts'])) return true;
    const name = submitter && (submitter.name || submitter.getAttribute('name'));
    return name === 'checkout' && storePath(destination, ['cart']);
  }
  function stopCheckout(event) {
    const cfg = config();
    if (cfg && cfg.mode === 'active' && cfg.price > 0 && (pending || running || unresolved)) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (lastCart) render(lastCart, cfg, preference(lastCart), unresolved);
      if (!unresolved) {
        for (const node of doc.querySelectorAll('[data-skrt-gwp]')) {
          const message = node.getAttribute('data-gift-checking');
          const text = node.querySelector('.skrt-gwp__msg');
          if (message && text) text.textContent = message;
        }
      }
      // Flush a pending debounce immediately. A second deliberate checkout action is
      // required after recovery; never replay navigation after an uncertain request.
      root.clearTimeout(timer); timer = null;
      if (!running && !externalOperations.size) synchronize();
    }
  }
  doc.addEventListener('submit', (event) => {
    if (checkoutSubmit(event)) stopCheckout(event);
  }, true);
  doc.addEventListener('click', (event) => {
    const target = event.target.closest && event.target.closest('[data-skrt-gift-restore], [data-skrt-gift-acknowledge], [is="cart-remove-button"], [name="checkout"], a[href]');
    if (!target) return;
    if (target.hasAttribute('data-skrt-gift-restore')) {
      event.preventDefault();
      manualDismissal = false; retryRequested = true; schedule();
    } else if (target.hasAttribute('data-skrt-gift-acknowledge')) {
      event.preventDefault();
      const cfg = config();
      if (cfg && lastCart) {
        const review = reviewGift(lastCart, cfg, preference(lastCart));
        if (review.required) savePreference(lastCart, { acknowledgedReview: review.key });
      }
      schedule();
    } else if (target.getAttribute('is') === 'cart-remove-button' && giftKey(target.getAttribute('data-index'))) {
      manualDismissal = true; schedule();
    } else if ((target.getAttribute('name') === 'checkout' && (!target.form ||
      storePath(target.form.getAttribute('action'), ['cart', 'checkout', 'checkouts']))) ||
      storePath(target.getAttribute('href'), ['checkout', 'checkouts'])) {
      stopCheckout(event);
    }
  }, true);
  doc.addEventListener('change', (event) => {
    const target = event.target;
    if (target.matches && target.matches('input[name="updates[]"]') && giftKey(target.getAttribute('data-index')) && Number(target.value) === 0) {
      manualDismissal = true; schedule();
    }
  }, true);
  doc.addEventListener('skrt:cart-operation-start', (event) => {
    const id = event.detail && event.detail.id;
    if (typeof id !== 'string' || !id) return;
    externalOperations.add(id);
    root.clearTimeout(timer); timer = null;
    pending = true; busy(true);
  });
  doc.addEventListener('skrt:cart-operation-end', (event) => {
    const id = event.detail && event.detail.id;
    if (externalOperations.delete(id) && !externalOperations.size) schedule();
  });
  for (const type of ['cart:updated', 'ajaxProduct:added', 'cart:refresh', 'shopify:section:load']) doc.addEventListener(type, schedule);
  doc.addEventListener('visibilitychange', () => { if (!doc.hidden) schedule(); });
  new MutationObserver((records) => {
    if (records.some((record) => Array.from(record.addedNodes).some((node) => node.nodeType === 1 &&
      (node.matches('[data-skrt-gwp]') || node.querySelector('[data-skrt-gwp]'))))) schedule();
  }).observe(doc.documentElement, { childList: true, subtree: true });
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
})(typeof window === 'undefined' ? null : window);
